/**
 * 숫자를 천 단위 콤마 포맷으로 변환
 * @example formatNumber('1120000') => '1,120,000'
 */
export function formatNumber(value: string): string {
  if (!value) return ''

  const digits = value.replace(/[^\d]/g, '')
  if (!digits) return ''

  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * 전화번호 입력값을 실시간으로 포맷
 * - 숫자만 남기고 하이픈(-)을 단계적으로 추가
 * - 국내: 010(휴대폰), 02(서울), 0xx(지역/070), 1588/1600/1800(대표번호), 050x(안심번호)
 * - 국제(+82): `+8210...` 형태도 허용하고 `+82-10-....`로 표시
 */
export function formatPhone(phone: string): string {
  if (!phone) return ''

  const raw = String(phone).trim()
  const isPlus = raw.startsWith('+')

  // 숫자만 남기고 과도한 길이는 잘라냄 (최대 12자리)
  const digits = raw.replace(/[^\d]/g, '').slice(0, 12)
  if (!digits) return ''

  if (isPlus && digits.startsWith('82')) {
    return formatKoreanInternational(digits)
  }

  return formatKoreanDomestic(digits)
}

/**
 * 국제 전화번호 포맷 (+82)
 */
function formatKoreanInternational(digits: string): string {
  // +82 제거 후 국내 번호로 변환
  const domestic = digits.slice(2)
  if (!domestic) return '+82'

  // 휴대폰 (10으로 시작)
  if (domestic.startsWith('10')) {
    const formatted = formatMobile(domestic)
    return `+82-${formatted}`
  }

  // 서울 (2로 시작)
  if (domestic.startsWith('2')) {
    const formatted = formatSeoul(domestic)
    return `+82-${formatted}`
  }

  // 일반 지역번호
  const formatted = formatRegional(domestic)
  return `+82-${formatted}`
}

/**
 * 국내 전화번호 포맷
 */
function formatKoreanDomestic(digits: string): string {
  // 휴대폰 (010, 011, 016, 017, 018, 019)
  if (/^01[0-9]/.test(digits)) {
    return formatMobile(digits)
  }

  // 서울 (02)
  if (digits.startsWith('02')) {
    return formatSeoul(digits)
  }

  // 대표번호 (1588, 1577, 1600, 1800 등)
  if (/^1[5-8]/.test(digits)) {
    return formatRepresentative(digits)
  }

  // 안심번호 (050x)
  if (digits.startsWith('050')) {
    return formatSafeNumber(digits)
  }

  // 070 인터넷전화
  if (digits.startsWith('070')) {
    return formatInternetPhone(digits)
  }

  // 일반 지역번호 (0xx)
  if (digits.startsWith('0')) {
    return formatRegional(digits)
  }

  return digits
}

/**
 * 휴대폰 포맷 (010-XXXX-XXXX)
 */
function formatMobile(digits: string): string {
  if (digits.length <= 3) return digits
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`
}

/**
 * 서울 지역번호 포맷 (02-XXX-XXXX 또는 02-XXXX-XXXX)
 */
function formatSeoul(digits: string): string {
  if (digits.length <= 2) return digits
  if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`
  if (digits.length <= 9)
    return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`
  return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}`
}

/**
 * 일반 지역번호 포맷 (0xx-XXX-XXXX 또는 0xx-XXXX-XXXX)
 */
function formatRegional(digits: string): string {
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  if (digits.length <= 10)
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`
}

/**
 * 대표번호 포맷 (1588-XXXX)
 */
function formatRepresentative(digits: string): string {
  if (digits.length <= 4) return digits
  return `${digits.slice(0, 4)}-${digits.slice(4, 8)}`
}

/**
 * 안심번호 포맷 (050x-XXX-XXXX 또는 050x-XXXX-XXXX)
 */
function formatSafeNumber(digits: string): string {
  if (digits.length <= 4) return digits
  if (digits.length <= 7) return `${digits.slice(0, 4)}-${digits.slice(4)}`
  if (digits.length <= 11)
    return `${digits.slice(0, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`
  return `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8, 12)}`
}

/**
 * 인터넷 전화 포맷 (070-XXXX-XXXX)
 */
function formatInternetPhone(digits: string): string {
  if (digits.length <= 3) return digits
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`
}
